import type { ReactNode } from 'react'
import React from 'react'
import { twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import Head from 'next/dist/shared/lib/head'
import { FAVICON_LINK_KEYS } from 'shared/constants'
import type { DomainAssetsInterface } from 'shared/types/domain'
import Header from 'modules/header'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import { useCourseData } from '../hooks/use-course-data'
import { useSetFonts } from '../hooks/use-set-fonts'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'

interface Props extends React.PropsWithChildren {
  domainAssets?: DomainAssetsInterface
}

function CoursePageLayout({ children, domainAssets }: Props): ReactNode {
  const { t } = useTranslation()

  const { brand } = useBrand()
  const { courseData } = useCourseData()
  const { fontStyle, fontFamily } = useSetFonts()
  const publicCustomClassNamePrefix = addPublicCustomClassNamePrefix('course__app')

  // TODO: research how to make oxfmt and better tailwind parse Classes in Header component
  // or better make it less absurd
  return (
    <div
      className={twJoin('min-h-screen bg-THEME-background', publicCustomClassNamePrefix)}
      style={fontFamily ? { fontFamily: `${fontFamily}, sans-serif` } : undefined}
    >
      <Head>
        <title>{courseData?.name || t('course.course_title')}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
        <style>{fontStyle}</style>
        <meta name="robots" content="noindex" />
      </Head>
      <Header
        logoSrc={courseData?.logo?.path || domainAssets?.logoUrl || brand.logo?.path}
        className="bg-THEME-header-background!"
        headerContainerClassName="mx-auto max-w-436.75 px-5 text-THEME-header-text! sm:px-6 md:px-5"
        desktopMenuItemClassName="text-THEME-header-text! hover:text-THEME-hover-header-text!"
        desktopActiveMenuItemClassName="text-THEME-hover-header-text!"
        menuIconClassName="text-THEME-header-text hover:text-THEME-hover-header-text"
        activeMenuIconClassName="text-THEME-hover-header-text"
        mobileMenuItemClassName="text-THEME-primary! hover:text-THEME-hover-primary! [&:hover>svg]:text-THEME-hover-primary! [&>svg]:text-THEME-primary!"
        burgerClassName="[&>path]:stroke-THEME-header-text"
      />
      <div
        className={`mx-auto max-w-436.75 p-5 sm:px-6 md:px-5 ${addPublicCustomClassNamePrefix('course__main')}`}
      >
        {children}
      </div>
    </div>
  )
}

export function getCoursePageLayout(
  page: ReactNode,
  props: { domainAssets?: DomainAssetsInterface },
): ReactNode {
  return <CoursePageLayout domainAssets={props.domainAssets}>{page}</CoursePageLayout>
}
